/* eslint-disable no-console */
import {
  captureMessage,
  getCurrentHub,
  init,
  makeBrowserOfflineTransport,
  makeFetchTransport,
  Replay
} from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { isElectron, isExternalSession } from '.';
import { version } from '../../package.json';

const initilizeSentry = () => {
  // generated from https://edukits.co/text-art/
  // eslint-disable-next-line no-console
  console.log(
    `
      ░▀█▀░█░█░█▀▄░█▀▀░█▀█░█▀▄░░░█▄█░█▀█░█▀▀░▀█▀░█▀▀░░░░░░░▀█▀░█▀█░█▀▀░░░
      ░░█░░█▀█░█▀▄░█▀▀░█▀█░█░█░░░█░█░█▀█░█░█░░█░░█░░░░░░░░░░█░░█░█░█░░░░░
      ░░▀░░▀░▀░▀░▀░▀▀▀░▀░▀░▀▀░░░░▀░▀░▀░▀░▀▀▀░▀▀▀░▀▀▀░▄▀░░░░▀▀▀░▀░▀░▀▀▀░▀░
      `
  );
  console.log(`Inbox v${version}, hash: ${__COMMIT_HASH__}`);
  console.log('Changelog: https://feedback.getthread.com/changelog');

  const dsn = isElectron()
    ? process.env?.REACT_APP_SENTRY_ELECTRON_DSN
    : process.env?.REACT_APP_SENTRY_DSN;

  init({
    dsn,
    integrations: [new BrowserTracing()],
    sampleRate: 0.5,
    tracesSampler: () => {
      // Exclude external sessions (e.g., ConnectWise, Autotask pods)
      // Single page load sessions don't provide enough data for tracking
      if (isExternalSession()) {
        return 0;
      }
      return 0.2;
    },
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    release: `${
      isElectron() ? window.require('electron').ipcRenderer.sendSync('app_version') : version
    }`,
    transport: makeBrowserOfflineTransport(makeFetchTransport),
    transportOptions: {
      shouldStore(_, error) {
        const ignoredErrors = ['Failed to fetch', 'Network Error'];
        if (ignoredErrors.includes(error?.message)) {
          return false;
        }
        return true;
      }
    },
    environment: process.env.REACT_APP_ENV,
    ignoreErrors: [
      'Non-Error exception captured',
      'Non-Error promise rejection captured with keys: currentTarget, isTrusted, target, type' // websocket error
    ]
  });
};

export const setupSessionReplay = ({ enabled }) => {
  const client = getCurrentHub().getClient();
  const isProduction = process.env.REACT_APP_ENV === 'production';
  if (!client || !isProduction) {
    return;
  }
  const currentReplayIntegration = client.getIntegration(Replay);
  if (enabled) {
    if (currentReplayIntegration) {
      // Case:
      // A member has 2 workspaces: A - Session Replay enabled, B - Session Replay disabled
      // The member switches workspace from A to B, so in B, session replay will be stopped
      // Then the member again switches workspace from B to A, there is already Session Replay integrated, and
      // it should be resumed, but this case (stop -> start) is not supported yet [https://github.com/getsentry/sentry-javascript/issues/7192]
      currentReplayIntegration.start();
      captureMessage('Attempt to resume session replay');
    } else {
      const replay = new Replay({
        maskAllText: false,
        maskAllInputs: false
      });
      client.addIntegration(replay);
    }
  } else if (currentReplayIntegration) {
    currentReplayIntegration.stop();
  }
};

export default initilizeSentry;
